import { useState } from 'react';
import { trpc } from '../lib/trpc';
import type { Media } from '@wineryxplorer/shared';
import { PageContainer } from '../components/PageContainer';

export function ApiTest() {
  // Email Testing states
  const [testEmail, setTestEmail] = useState('');
  
  // SMS Testing states
  const [phoneNumber, setPhoneNumber] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  
  const [venueId, setVenueId] = useState('');
  const [entityId, setEntityId] = useState('');
  
  // Venue filter states
  const [venueStatus, setVenueStatus] = useState<string>('');
  const [venueLimit, setVenueLimit] = useState<number>(10);
  const [venueSortBy, setVenueSortBy] = useState<string>('createdAt');
  const [venueSortOrder, setVenueSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Event filter states
  const [eventStatus, setEventStatus] = useState<string>('');
  const [eventAudience, setEventAudience] = useState<string>('');
  const [eventSortBy, setEventSortBy] = useState<string>('startDate');
  const [eventSortOrder, setEventSortOrder] = useState<'asc' | 'desc'>('asc');
  
  // Media filter states
  const [mediaStatus, setMediaStatus] = useState<string>('');
  const [mediaSortBy, setMediaSortBy] = useState<string>('createdAt');
  const [mediaSortOrder, setMediaSortOrder] = useState<'asc' | 'desc'>('desc');
  
  // Review filter states
  const [minRating, setMinRating] = useState<string>('');
  const [maxRating, setMaxRating] = useState<string>('');
  const [createdAfter, setCreatedAfter] = useState<string>('');
  const [createdBefore, setCreatedBefore] = useState<string>('');
  const [reviewSortBy, setReviewSortBy] = useState<string>('createdAt');
  const [reviewSortOrder, setReviewSortOrder] = useState<'asc' | 'desc'>('desc');

  // Venue queries
  const { data: venues, refetch: refetchVenues, error: venuesError } = trpc.venue.list.useQuery({
    limit: venueLimit,
    ...(venueStatus && { status: venueStatus as 'draft' | 'published' }),
    sortBy: venueSortBy as 'name' | 'createdAt' | 'updatedAt',
    sortOrder: venueSortOrder,
  });

  const { data: venue } = trpc.venue.getById.useQuery(
    { id: venueId },
    { enabled: !!venueId }
  );

  // Event queries
  const { data: events, error: eventsError } = trpc.event.list.useQuery({
    limit: 10,
    ...(eventStatus && { status: eventStatus as 'draft' | 'published' | 'cancelled' }),
    ...(eventAudience && { audience: eventAudience as 'public' | 'members_only' }),
    sortBy: eventSortBy as 'name' | 'startDate' | 'createdAt',
    sortOrder: eventSortOrder,
  });

  // Media queries
  const { data: mediaList, error: mediaError } = trpc.media.listByEntity.useQuery(
    {
      entityType: 'venue',
      entityId,
      ...(mediaStatus && { status: mediaStatus as 'active' | 'archived' }),
      sortBy: mediaSortBy as 'createdAt' | 'updatedAt',
      sortOrder: mediaSortOrder,
    },
    { enabled: !!entityId }
  );
  
  // Review queries
  const { data: reviews, error: reviewsError } = trpc.review.list.useQuery({
    limit: 10,
    ...(minRating && { minRating: parseInt(minRating) }),
    ...(maxRating && { maxRating: parseInt(maxRating) }),
    ...(createdAfter && { createdAfter }),
    ...(createdBefore && { createdBefore }),
    sortBy: reviewSortBy as 'rating' | 'createdAt',
    sortOrder: reviewSortOrder,
  });

  const getUploadSig = trpc.media.getUploadSignature.useMutation();
  
  // Email Testing mutation
  const testEmailMutation = trpc.auth.testEmail.useMutation();
  
  // SMS Testing mutations
  const sendPhoneCode = trpc.auth.sendPhoneCode.useMutation();
  const verifyPhoneCode = trpc.auth.verifyPhoneCode.useMutation();

  return (
    <PageContainer variant="white" className="space-y-8">
      <h1 className="text-3xl font-bold">API Test Page</h1>
      
      {/* Email Testing Section */}
      <section className="p-6 border-2 border-primary-500 rounded-lg bg-primary-50">
        <h2 className="text-2xl font-semibold mb-4 text-primary-900">Email Verification Testing</h2>
        
        <div className="space-y-6">
          <div className="bg-gray-50 p-4 rounded border">
            <h3 className="font-semibold mb-3">Send Test Verification Email</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Email Address</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-3 py-2 border rounded"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter your email address to receive a test verification email
                </p>
              </div>
              
              <button
                onClick={() => testEmailMutation.mutate({ email: testEmail })}
                disabled={testEmailMutation.isPending || !testEmail}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-400"
              >
                {testEmailMutation.isPending ? 'Sending...' : 'Send Test Email'}
              </button>
              
              {testEmailMutation.isSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800">
                  ✅ {testEmailMutation.data.message}
                </div>
              )}
              
              {testEmailMutation.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800">
                  ❌ Error: {testEmailMutation.error.message}
                </div>
              )}
            </div>
          </div>
          
          {/* Important Notes */}
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">⚠️ SendGrid Configuration Required:</h4>
            <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
              <li>Add your SendGrid API key to SMTP_PASS in .env</li>
              <li>Add your verified sender email to EMAIL_FROM in .env</li>
              <li>Verify your sender email in SendGrid Console</li>
              <li>Free tier: 100 emails/day</li>
            </ul>
          </div>
        </div>
      </section>
      
      {/* SMS Testing Section */}
      <section className="p-6 border-2 border-primary-500 rounded-lg bg-primary-50">
        <h2 className="text-2xl font-semibold mb-4 text-primary-900">SMS / Phone Authentication Testing</h2>
        
        <div className="space-y-6">
          {/* Send Code */}
          <div className="bg-gray-50 p-4 rounded border">
            <h3 className="font-semibold mb-3">Step 1: Send Verification Code</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Phone Number (E.164 format)</label>
                <input
                  type="text"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+15551234567"
                  className="w-full px-3 py-2 border rounded"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Format: +[country code][number] (e.g., +15551234567 for US)
                </p>
              </div>
              
              <button
                onClick={() => sendPhoneCode.mutate({ phoneNumber })}
                disabled={sendPhoneCode.isPending || !phoneNumber}
                className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:bg-gray-400"
              >
                {sendPhoneCode.isPending ? 'Sending...' : 'Send Code'}
              </button>
              
              {sendPhoneCode.isSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-green-800">
                  ✅ Code sent! Check your phone for the verification code.
                </div>
              )}
              
              {sendPhoneCode.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800">
                  ❌ Error: {sendPhoneCode.error.message}
                </div>
              )}
            </div>
          </div>
          
          {/* Verify Code */}
          <div className="bg-gray-50 p-4 rounded border">
            <h3 className="font-semibold mb-3">Step 2: Verify Code</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Verification Code</label>
                <input
                  type="text"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              
              <button
                onClick={() => verifyPhoneCode.mutate({ phoneNumber, code: verificationCode })}
                disabled={verifyPhoneCode.isPending || !phoneNumber || !verificationCode}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
              >
                {verifyPhoneCode.isPending ? 'Verifying...' : 'Verify Code'}
              </button>
              
              {verifyPhoneCode.isSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded">
                  <div className="text-green-800 font-semibold mb-2">✅ Verification successful!</div>
                  <pre className="text-xs bg-gray-50 p-2 rounded border overflow-auto">
                    {JSON.stringify(verifyPhoneCode.data, null, 2)}
                  </pre>
                </div>
              )}
              
              {verifyPhoneCode.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800">
                  ❌ Error: {verifyPhoneCode.error.message}
                </div>
              )}
            </div>
          </div>
          
          {/* Important Notes */}
          <div className="bg-yellow-50 border border-yellow-200 rounded p-4">
            <h4 className="font-semibold text-yellow-900 mb-2">⚠️ Important Notes:</h4>
            <ul className="text-sm text-yellow-800 space-y-1 list-disc list-inside">
              <li>Twilio trial accounts can only send to verified phone numbers</li>
              <li>Verify your number in Twilio Console → Phone Numbers → Verified Caller IDs</li>
              <li>Rate limit: 5 SMS per phone number per hour</li>
              <li>Phone number must be in E.164 format (+[country code][number])</li>
            </ul>
          </div>
        </div>
      </section>
      
      {/* Documentation Section */}
      <section className="mb-8 p-4 border rounded bg-primary-50">
        <h2 className="text-xl font-semibold mb-4">API Flexibility Improvements</h2>
        <p className="text-sm text-gray-700 mb-4">
          This page demonstrates the new flexible filtering, sorting, and pagination capabilities
          added to the API. All filters are optional and explicit - no hidden defaults.
        </p>
        <div className="space-y-2 text-sm">
          <div><strong>Status Filtering:</strong> Filter by status (draft, published, archived, cancelled)</div>
          <div><strong>Sorting:</strong> Sort by various fields with asc/desc order</div>
          <div><strong>Pagination:</strong> Control limit and offset (max 100 items)</div>
          <div><strong>Permission-Aware:</strong> Some filters require admin permissions</div>
        </div>
      </section>

      {/* Venue List */}
      <section className="mb-8 p-4 border rounded">
        <h2 className="text-xl font-semibold mb-4">Venues</h2>
        
        {/* Venue Filters */}
        <div className="mb-4 p-4 bg-gray-50 rounded space-y-3">
          <h3 className="font-medium text-sm text-gray-700">Filters & Options</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Status (Admin Only)</label>
              <select
                value={venueStatus}
                onChange={(e) => setVenueStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="">All (default: published for non-admins)</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Limit</label>
              <input
                type="number"
                value={venueLimit}
                onChange={(e) => setVenueLimit(parseInt(e.target.value) || 10)}
                className="w-full px-3 py-2 border rounded text-sm"
                min="1"
                max="100"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort By</label>
              <select
                value={venueSortBy}
                onChange={(e) => setVenueSortBy(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="createdAt">Created Date</option>
                <option value="updatedAt">Updated Date</option>
                <option value="name">Name</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort Order</label>
              <select
                value={venueSortOrder}
                onChange={(e) => setVenueSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
        
        <button
          onClick={() => refetchVenues()}
          className="px-4 py-2 bg-primary-500 text-white rounded mb-4 text-sm"
        >
          Refresh Venues
        </button>
        
        {venuesError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">
            <strong>Error:</strong> {venuesError.message}
            {venuesError.message.includes('FORBIDDEN') && (
              <div className="mt-2 text-xs">
                💡 This filter requires admin permissions. Try without status filter or log in as admin.
              </div>
            )}
          </div>
        )}
        
        <div className="space-y-2">
          {venues?.items.map((v: { _id: string; name: string; venueType: string; status?: string }) => (
            <div key={v._id} className="p-2 bg-gray-100 rounded">
              <div className="font-medium">{v.name}</div>
              <div className="text-sm text-gray-600">
                {v.venueType} {v.status && `• ${v.status}`}
              </div>
              <button
                onClick={() => setVenueId(v._id)}
                className="text-primary-500 text-sm mt-1"
              >
                Load Details
              </button>
            </div>
          ))}
          {venues && (
            <div className="text-sm text-gray-600 mt-2">
              Showing {venues.items.length} of {venues.total} venues
            </div>
          )}
        </div>
      </section>

      {/* Venue Detail */}
      {venue && (
        <section className="mb-8 p-4 border rounded">
          <h2 className="text-xl font-semibold mb-4">Venue Detail</h2>
          <pre className="bg-gray-100 p-4 rounded overflow-auto text-xs">
            {JSON.stringify(venue, null, 2)}
          </pre>
        </section>
      )}
      
      {/* Events List */}
      <section className="mb-8 p-4 border rounded">
        <h2 className="text-xl font-semibold mb-4">Events</h2>
        
        {/* Event Filters */}
        <div className="mb-4 p-4 bg-gray-50 rounded space-y-3">
          <h3 className="font-medium text-sm text-gray-700">Filters & Options</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Status</label>
              <select
                value={eventStatus}
                onChange={(e) => setEventStatus(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="">All (no default)</option>
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Audience</label>
              <select
                value={eventAudience}
                onChange={(e) => setEventAudience(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="">All</option>
                <option value="public">Public</option>
                <option value="members_only">Members Only</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort By</label>
              <select
                value={eventSortBy}
                onChange={(e) => setEventSortBy(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="startDate">Start Date</option>
                <option value="createdAt">Created Date</option>
                <option value="name">Name</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort Order</label>
              <select
                value={eventSortOrder}
                onChange={(e) => setEventSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="asc">Ascending</option>
                <option value="desc">Descending</option>
              </select>
            </div>
          </div>
        </div>
        
        {eventsError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">
            <strong>Error:</strong> {eventsError.message}
          </div>
        )}
        
        <div className="space-y-2">
          {(events?.items as unknown as Array<{ _id: string; name: string; status?: string; audience?: string; startDate?: string }>)?.map((e) => (
            <div key={e._id} className="p-2 bg-gray-100 rounded">
              <div className="font-medium">{e.name}</div>
              <div className="text-sm text-gray-600">
                {e.status && `${e.status} • `}
                {e.audience === 'members_only' ? 'Members Only' : 'Public'}
                {e.startDate && ` • ${new Date(e.startDate).toLocaleDateString()}`}
              </div>
            </div>
          ))}
          {events && (
            <div className="text-sm text-gray-600 mt-2">
              Showing {events.items.length} of {events.total} events
            </div>
          )}
        </div>
      </section>
      
      {/* Reviews List */}
      <section className="mb-8 p-4 border rounded">
        <h2 className="text-xl font-semibold mb-4">Reviews</h2>
        
        {/* Review Filters */}
        <div className="mb-4 p-4 bg-gray-50 rounded space-y-3">
          <h3 className="font-medium text-sm text-gray-700">Filters & Options</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium mb-1">Min Rating (1-5)</label>
              <input
                type="number"
                value={minRating}
                onChange={(e) => setMinRating(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                min="1"
                max="5"
                placeholder="No minimum"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Max Rating (1-5)</label>
              <input
                type="number"
                value={maxRating}
                onChange={(e) => setMaxRating(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
                min="1"
                max="5"
                placeholder="No maximum"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Created After</label>
              <input
                type="date"
                value={createdAfter}
                onChange={(e) => setCreatedAfter(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Created Before</label>
              <input
                type="date"
                value={createdBefore}
                onChange={(e) => setCreatedBefore(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort By</label>
              <select
                value={reviewSortBy}
                onChange={(e) => setReviewSortBy(e.target.value)}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="createdAt">Created Date</option>
                <option value="rating">Rating</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium mb-1">Sort Order</label>
              <select
                value={reviewSortOrder}
                onChange={(e) => setReviewSortOrder(e.target.value as 'asc' | 'desc')}
                className="w-full px-3 py-2 border rounded text-sm"
              >
                <option value="desc">Descending</option>
                <option value="asc">Ascending</option>
              </select>
            </div>
          </div>
        </div>
        
        {reviewsError && (
          <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">
            <strong>Error:</strong> {reviewsError.message}
          </div>
        )}
        
        <div className="space-y-2">
          {(reviews?.items as unknown as Array<{ _id: string; rating: number; comment?: string; createdAt?: string }>)?.map((r) => (
            <div key={r._id} className="p-2 bg-gray-100 rounded">
              <div className="font-medium">Rating: {r.rating}/5</div>
              {r.comment && <div className="text-sm text-gray-600">{r.comment}</div>}
              {r.createdAt && (
                <div className="text-xs text-gray-500 mt-1">
                  {new Date(r.createdAt).toLocaleDateString()}
                </div>
              )}
            </div>
          ))}
          {reviews && (
            <div className="text-sm text-gray-600 mt-2">
              Showing {reviews.items.length} of {reviews.total} reviews
            </div>
          )}
        </div>
      </section>

      {/* Media Test */}
      <section className="mb-8 p-4 border rounded">
        <h2 className="text-xl font-semibold mb-4">Media API Test</h2>
        
        <div className="mb-4">
          <label className="block text-sm font-medium mb-2">
            Entity ID (Venue ID):
          </label>
          <input
            type="text"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            className="w-full px-3 py-2 border rounded"
            placeholder="Enter venue ID"
          />
        </div>
        
        {/* Media Filters */}
        {entityId && (
          <div className="mb-4 p-4 bg-gray-50 rounded space-y-3">
            <h3 className="font-medium text-sm text-gray-700">Filters & Options</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Status</label>
                <select
                  value={mediaStatus}
                  onChange={(e) => setMediaStatus(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="">All (default: active)</option>
                  <option value="active">Active</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Sort By</label>
                <select
                  value={mediaSortBy}
                  onChange={(e) => setMediaSortBy(e.target.value)}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="createdAt">Created Date</option>
                  <option value="updatedAt">Updated Date</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1">Sort Order</label>
                <select
                  value={mediaSortOrder}
                  onChange={(e) => setMediaSortOrder(e.target.value as 'asc' | 'desc')}
                  className="w-full px-3 py-2 border rounded text-sm"
                >
                  <option value="desc">Descending</option>
                  <option value="asc">Ascending</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {entityId && (
          <>
            <button
              onClick={() =>
                getUploadSig.mutate({
                  entityType: 'venue',
                  entityId,
                })
              }
              className="px-4 py-2 bg-green-500 text-white rounded mb-4 text-sm"
            >
              Get Upload Signature
            </button>

            {getUploadSig.data && (
              <div className="mb-4 p-4 bg-green-50 rounded">
                <div className="font-medium mb-2">Upload Signature:</div>
                <pre className="text-xs overflow-auto">
                  {JSON.stringify(getUploadSig.data, null, 2)}
                </pre>
              </div>
            )}

            {getUploadSig.error && (
              <div className="mb-4 p-4 bg-red-50 text-red-700 rounded text-sm">
                Error: {getUploadSig.error.message}
              </div>
            )}
            
            {mediaError && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded text-sm">
                <strong>Error:</strong> {mediaError.message}
              </div>
            )}

            <div className="mt-4">
              <div className="font-medium mb-2">Media for this venue:</div>
              {mediaList?.items.length === 0 && (
                <div className="text-gray-500 text-sm">No media found</div>
              )}
              {mediaList?.items && (mediaList.items as unknown as Media[]).map((m) => (
                <div key={m.id} className="p-2 bg-gray-100 rounded mb-2">
                  <div className="text-sm">
                    <strong>Entity Type:</strong> {m.entityType}
                  </div>
                  <div className="text-sm">
                    <strong>Public ID:</strong> {m.cloudinaryPublicId}
                  </div>
                  <div className="text-sm">
                    <strong>Status:</strong> {m.status}
                  </div>
                </div>
              ))}
              {mediaList && (
                <div className="text-sm text-gray-600 mt-2">
                  Showing {mediaList.items.length} of {mediaList.total} media items
                </div>
              )}
            </div>
          </>
        )}
      </section>
      
      {/* Permission Testing Examples */}
      <section className="mb-8 p-4 border rounded bg-yellow-50">
        <h2 className="text-xl font-semibold mb-4">Permission Testing Scenarios</h2>
        <div className="space-y-4 text-sm">
          <div>
            <h3 className="font-medium mb-2">Scenario 1: Non-Admin Status Filter</h3>
            <p className="text-gray-700 mb-2">
              Try selecting a status filter for venues (e.g., "Draft" or "Archived") without admin permissions.
            </p>
            <p className="text-gray-600 italic">
              Expected: FORBIDDEN error - "You do not have permission to filter by status"
            </p>
          </div>
          
          <div>
            <h3 className="font-medium mb-2">Scenario 2: Admin Status Filter</h3>
            <p className="text-gray-700 mb-2">
              Log in as an admin and try the same status filters.
            </p>
            <p className="text-gray-600 italic">
              Expected: Success - see venues with the selected status
            </p>
          </div>
          
          <div>
            <h3 className="font-medium mb-2">Scenario 3: Default Behavior</h3>
            <p className="text-gray-700 mb-2">
              Clear all filters and refresh venues without admin permissions.
            </p>
            <p className="text-gray-600 italic">
              Expected: Only published venues are shown (default for non-admins)
            </p>
          </div>
          
          <div>
            <h3 className="font-medium mb-2">Scenario 4: Combined Filters</h3>
            <p className="text-gray-700 mb-2">
              Try combining rating filters (min/max) with date filters on reviews.
            </p>
            <p className="text-gray-600 italic">
              Expected: Reviews matching ALL specified criteria
            </p>
          </div>
          
          <div>
            <h3 className="font-medium mb-2">Scenario 5: Sorting</h3>
            <p className="text-gray-700 mb-2">
              Change sort order and sort by fields for any entity type.
            </p>
            <p className="text-gray-600 italic">
              Expected: Results reordered according to selected sort criteria
            </p>
          </div>
        </div>
      </section>
    </PageContainer>
  );
}
